// ── Enums / string unions ──────────────────────────────────────────────────────

export type PaymentMethod =
  | 'Cash'
  | 'Cheque'
  | 'BankTransfer'
  | 'CreditCard'
  | 'DebitCard'
  | 'OnlineBanking'
  | 'Other';

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  Cash:          'Cash',
  Cheque:        'Cheque',
  BankTransfer:  'Bank Transfer',
  CreditCard:    'Credit Card',
  DebitCard:     'Debit Card',
  OnlineBanking: 'Online Banking',
  Other:         'Other',
};

// ── Line DTO ──────────────────────────────────────────────────────────────────

export interface ArReceiptLineDto {
  id:            string;
  receiptId:     string;
  lineNumber:    number;
  paymentType:   PaymentMethod;
  bankAccountId?: string | null;
  /** Cash GL account this line draws on. Null for bank methods, which resolve the GL
   *  account through the bank account instead. */
  fundingGlAccountId?: string | null;
  chequeNumber?: string | null;
  chequeDate?:   string | null;
  /** The customer's bank and branch this cheque is drawn on (GAP-014) — not bankAccountId (ours). */
  drawerBankId?: string | null;
  drawerBranchId?: string | null;
  cardReference?: string | null;
  amount:        number;
  baseAmount:    number;
  currencyCode:  string;
  exchangeRate:  number;
  remarks?:      string | null;
}

// ── Detail DTO ────────────────────────────────────────────────────────────────

export interface ArReceiptDto {
  id:                   string;
  companyId:            string;
  locationId?:          string | null;
  costCenterId?:        string | null;
  financialYearCode:    string;
  financialPeriodCode:  string;
  customerId:           string;
  customerCode:         string;
  customerName:         string;
  controlAccountId?:    string | null;
  receiptNumber:        string;
  receiptDate:          string;
  postingDate:          string;
  reference?:           string | null;
  narration?:           string | null;
  paymentMethod:        PaymentMethod;
  bankAccountId?:       string | null;
  currencyCode:         string;
  exchangeRate:         number;
  workflowStatus:       string;
  postingStatus:        string;
  totalAmount:          number;
  baseAmount:           number;
  submittedBy?:         string | null;
  submittedDate?:       string | null;
  approvedBy?:          string | null;
  approvedDate?:        string | null;
  rejectedBy?:          string | null;
  rejectedDate?:        string | null;
  rejectionReason?:     string | null;
  postedBy?:            string | null;
  postedDate?:          string | null;
  reversedBy?:          string | null;
  reversedDate?:        string | null;
  isReversal:           boolean;
  originalReceiptId?:   string | null;
  reversalReceiptId?:   string | null;
  sourceGlJournalId?:   string | null;
  createdBy?:           string | null;
  createdDate:          string;
  modifiedBy?:          string | null;
  modifiedDate?:        string | null;
  lines:                ArReceiptLineDto[];
  /** Invoices this receipt settles. Empty for an on-account receipt. */
  allocations?:         ArReceiptAllocationDto[] | null;
  /** The withholding rule applied, if any. totalAmount is then the gross settled; the money
   *  that actually arrived is totalAmount minus withholdingAmount. */
  withholdingTaxRuleId?: string | null;
  withholdingRuleCode?: string | null;
  withholdingPercent?:  number;
  withholdingAmount?:   number;
}

// ── List row DTO ──────────────────────────────────────────────────────────────

export interface ArReceiptListDto {
  id:             string;
  receiptNumber:  string;
  receiptDate:    string;
  postingDate:    string;
  customerCode:   string;
  customerName:   string;
  paymentMethod:  string;
  currencyCode:   string;
  totalAmount:    number;
  baseAmount:     number;
  workflowStatus: string;
  postingStatus:  string;
  reference?:     string | null;
  createdDate:    string;
}

// ── Filter DTO ────────────────────────────────────────────────────────────────

export interface ArReceiptFilterDto {
  companyId?:          string;
  locationId?:         string;
  costCenterId?:       string;
  customerId?:         string;
  customerCode?:       string;
  receiptNumber?:      string;
  workflowStatus?:     string;
  postingStatus?:      string;
  financialYearCode?:  string;
  financialPeriodCode?: string;
  dateFrom?:           string;
  dateTo?:             string;
  paymentMethod?:      string;
  reference?:          string;
  search?:             string;
  pageNumber?:         number;
  pageSize?:           number;
  sortBy?:             string;
  sortDescending?:     boolean;
}

// ── Line request ──────────────────────────────────────────────────────────────

export interface ArReceiptLineRequest {
  lineNumber:     number;
  paymentType:    string;
  bankAccountId?: string | null;
  /** Cash GL account this line draws on. Null for bank methods, which resolve the GL
   *  account through the bank account instead. */
  fundingGlAccountId?: string | null;
  chequeNumber?:  string | null;
  chequeDate?:    string | null;
  /** The customer's bank and branch this cheque is drawn on (GAP-014) — not bankAccountId (ours). */
  drawerBankId?:  string | null;
  drawerBranchId?: string | null;
  cardReference?: string | null;
  amount:         number;
  currencyCode:   string;
  exchangeRate:   number;
  remarks?:       string | null;
}

// ── Create / Update requests ──────────────────────────────────────────────────

/** An invoice this receipt settles, chosen on the voucher. Applied server-side at posting. */
export interface ArReceiptAllocationRequest {
  openItemId:      string;
  allocatedAmount: number;
  remarks?:        string | null;
}

export interface ArReceiptAllocationDto {
  id:              string;
  lineNumber:      number;
  openItemId:      string;
  documentNumber:  string;
  documentDate?:   string | null;
  dueDate?:        string | null;
  allocatedAmount: number;
  remarks?:        string | null;
}

export interface CreateArReceiptRequest {
  locationId?:      string | null;
  locationCode?:    string | null;
  costCenterId?:    string | null;
  customerId:       string;
  customerCode:     string;
  customerName:     string;
  controlAccountId?: string | null;
  receiptDate:      string;
  postingDate:      string;
  paymentMethod:    string;
  bankAccountId?:   string | null;
  currencyCode:     string;
  exchangeRate:     number;
  reference?:       string | null;
  narration?:       string | null;
  lines:            ArReceiptLineRequest[];
  /** Omit for an on-account receipt. */
  allocations?:     ArReceiptAllocationRequest[];
  withholdingTaxRuleId?: string | null;
}

export interface UpdateArReceiptRequest {
  locationId?:      string | null;
  costCenterId?:    string | null;
  customerId:       string;
  customerCode:     string;
  customerName:     string;
  controlAccountId?: string | null;
  receiptDate:      string;
  postingDate:      string;
  paymentMethod:    string;
  bankAccountId?:   string | null;
  currencyCode:     string;
  exchangeRate:     number;
  reference?:       string | null;
  narration?:       string | null;
  lines:            ArReceiptLineRequest[];
  /** Voucher settles these. Omit for on-account. */
  allocations?:     ArReceiptAllocationRequest[];
  withholdingTaxRuleId?: string | null;
}
